<template>
  <BaseSpinner v-if="adyenDashboardDataLoading" />
  <div
    v-else
    :class="[
      $style.base,
      { [$style.smallScreen]: $screen === 's' || $screen === 'm' }
    ]"
    v-test="'sp-dashboard'"
  >
    <PayoutSchedule
      v-if="showPayoutSchedule"
      @close="showPayoutSchedule = false"
    />

    <BaseVisualBanner
      v-if="
        adyenDashboardDataLength > 0 && !adyenDashboardData.hasPosTransaction
      "
      :heading="$t('register.salonized_pay.dashboard.complete_checkout', brandNamePay())"
      imagePath="/img/salonized-pay/register.svg"
      :secondaryAction="{
        text: $t('register.salonized_pay.dashboard.register'),
        routerLink: { name: 'register' }
      }"
      theme="dark"
      color="green"
      v-test="'sp-new-transaction-banner'"
    />

    <BaseVisualBanner
      v-if="showOrderTerminalBanner"
      :heading="
        $t('register.salonized_pay.dashboard.order_terminal_banner.heading', brandNamePay())
      "
      imagePath="/img/salonized-pay/order.svg"
      :secondaryAction="
        $t('register.salonized_pay.dashboard.order_terminal_banner.button')
      "
      theme="dark"
      color="green"
      @secondaryAction="handleCheckoutModal"
      v-test="'sp-order-terminal-banner'"
    />

    <BaseVisualBanner
      v-if="showTerminalOrderConfirmedBanner"
      :heading="$t('register.salonized_pay.dashboard.order_complete')"
      :description="
        $t('register.salonized_pay.dashboard.order_complete_description', brandName())
      "
      imagePath="/img/salonized-pay/order.svg"
      color="green"
      v-test="'sp-new-terminal-order-banner'"
    />

    <BaseVisualBanner
      v-else-if="showTerminalDeliveredBanner"
      :heading="$t('register.salonized_pay.dashboard.terminal_delivered.heading')"
      :description="
        $t('register.salonized_pay.dashboard.terminal_delivered.description')
      "
      imagePath="/img/salonized-pay/order.svg"
      color="green"
      theme="dark"
      :secondaryAction="{
        text: $t('register.salonized_pay.dashboard.terminal_delivered.button_text'),
        href: helpCenterUrl()
      }"
      v-test="'sp-terminal-delivered-banner'"
    />

    <div :class="$style.container">
      <div :class="$style.containerLeft">
        <LeftNav
          v-model:section="section"
          :verified="!adyenAppStateLoaded || adyenAppState.verificationStatus === 'VALID'"
        />
      </div>
      <Transition
        name="section-fade"
        mode="out-in"
      >
        <div
          v-if="section === 'payouts'"
          :class="$style.containerRight"
        >
          <Payouts
            :availableBalance="adyenDashboardData?.availableBalance || 0"
            :processingBalance="adyenDashboardData?.processingBalance || 0"
            :initialBalanceAccountId="payoutsBalanceAccountId"
            @showPayoutSchedule="showPayoutSchedule = true"
          />
        </div>
        <div
          v-else-if="section === 'wallets'"
          :class="$style.containerRight"
        >
          <Wallets
            @generateOnboardingLink="generateAdyenOnboardingLink"
            @viewPayouts="viewWalletPayouts"
          />
        </div>
        <div
          v-else-if="section === 'terminals'"
          :class="$style.containerRight"
        >
          <TerminalsSection
            :terminals="adyenDashboardData?.terminals || []"
            :terminalOrders="adyenDashboardData?.terminalOrders || []"
            :hasTerminal="!!hasTerminal"
            :showAddTerminal="!!showTerminalOrderTask"
            :terminalsUnavailable="showTerminalsUnavailable"
            @addTerminal="handleCheckoutModal"
            @editTipping="section = 'settings'"
          />
        </div>
        <div
          v-else-if="section === 'business-details'"
          :class="$style.containerRight"
        >
          <BusinessDetails
            @generateOnboardingLink="generateAdyenOnboardingLink"
          />
        </div>
        <div
          v-else-if="section === 'settings'"
          :class="$style.containerRight"
        >
          <Settings />
        </div>
        <div
          v-else-if="section === 'help'"
          :class="$style.containerRight"
        >
          <Help />
        </div>
        <div
          v-else
          :class="$style.containerRight"
        >
          <Overview
            :availableBalance="adyenDashboardData?.availableBalance || 0"
            :processingBalance="adyenDashboardData?.processingBalance || 0"
            :showBalance="showBalance"
            :hasData="adyenDashboardDataLength > 0"
            :isVerifying="adyenDashboardDataLength < 1"
            :hasTerminal="!!hasTerminal"
            :shippingTerminals="shippingTerminals"
            :shippingTerminalOrders="shippingTerminalOrders"
            :loading="loading"
            @generateOnboardingLink="generateAdyenOnboardingLink"
            @seeAllPayouts="section = 'payouts'"
            @seeAllTerminals="section = 'terminals'"
          />
        </div>
      </Transition>
    </div>
    <OrderTerminalModal
      v-if="showOrderTerminalModal"
      @close="showOrderTerminalModal = false"
    />

    <SubscriptionRequiredModal
      v-if="showSubscriptionRequiredModal"
      @close="showSubscriptionRequiredModal = false"
    />

    <SPSuccessModal
      v-if="showSuccessModal"
      @close="showSuccessModal = false"
    />
  </div>
</template>

<script
  lang="ts"
  setup
>
import { useQuery } from '@vue/apollo-composable';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import dayjs from '@/dayjs';
import { brandName, brandNamePay } from '@/helpers/brand-name';
import { helpCenterUrl } from '@/helpers/help-center';
import { scrollPage } from '@/helpers/scroll';
import { useAdyenStore } from '@/stores/adyen';
import { useCompanyStore } from '@/stores/company';
import { useUserStore } from '@/stores/user';
import {
  type AdyenTerminalOrder,
  AdyenTerminalOrderStatusEnum,
  type PosTerminal,
} from '@/types';
import { generateOnboardingLink } from '../../helpers';
import {
  GET_ADYEN_DASHBOARD_DATA,
  GET_ADYEN_DASHBOARD_DATA_WITHOUT_BALANCE,
} from '../graphql';
import BusinessDetails from './BusinessDetails.vue';
import Help from './Help.vue';
import LeftNav from './LeftNav.vue';
import Overview from './Overview.vue';
import OrderTerminalModal from './order-terminal-modal/index.vue';
import SPSuccessModal from './order-terminal-modal/SPSuccessModal.vue';
import PayoutSchedule from './PayoutSchedule.vue';
import Payouts from './Payouts.vue';
import Settings from './Settings.vue';
import SubscriptionRequiredModal from './SubscriptionRequiredModal.vue';
import TerminalsSection from './TerminalsSection.vue';
import Wallets from './Wallets.vue';

const loading = ref(false);
const section = ref<
  | 'overview'
  | 'payouts'
  | 'wallets'
  | 'terminals'
  | 'business-details'
  | 'settings'
  | 'help'
>('overview');

// The wallet the payout history opens filtered on, set from a wallet card.
const payoutsBalanceAccountId = ref<number | null>(null);

const viewWalletPayouts = (balanceAccountId: number) => {
  payoutsBalanceAccountId.value = balanceAccountId;
  section.value = 'payouts';
};

watch(section, (newSection) => {
  scrollPage({ position: 'top' });

  // Opening Payouts any other way shows every wallet again.
  if (newSection !== 'payouts') {
    payoutsBalanceAccountId.value = null;
  }
});

const showPayoutSchedule = ref(false);
const showSuccessModal = ref(false);

const router = useRouter();
const route = useRoute();

const showOrderTerminalModal = ref(false);
const showSubscriptionRequiredModal = ref(false);

const { company, isTrial } = useCompanyStore();
const { hasFeatureFlag } = useUserStore();
const { adyenAppState, adyenAppStateLoaded } = useAdyenStore();

const handleCheckoutModal = () => {
  const isAppointmentsPlan =
    company.activeSubscription?.plan === 'appointments';
  if (isAppointmentsPlan || isTrial) {
    // Show subscription required modal instead
    showSubscriptionRequiredModal.value = true;
  } else {
    // Show normal order terminal modal
    showOrderTerminalModal.value = true;
  }
};

const showBalance = computed(() => hasFeatureFlag('module-reports'));

const { result: dashboardDataResult, loading: adyenDashboardDataLoading } =
  useQuery(
    showBalance.value
      ? GET_ADYEN_DASHBOARD_DATA
      : GET_ADYEN_DASHBOARD_DATA_WITHOUT_BALANCE,
  );

const adyenDashboardData = computed(
  () => dashboardDataResult.value?.adyenDashboardData,
);

const adyenDashboardDataLength = computed(
  () =>
    adyenDashboardDataLoading &&
    adyenDashboardData.value &&
    adyenDashboardData.value.terminals.length,
);
const hasTerminal = computed(
  () =>
    adyenDashboardData.value?.terminals?.length ||
    adyenDashboardData.value?.lastTerminalOrderAt,
);

// Orders on their way, so their tracking shows on the Overview until delivered.
const isShipping = (terminalOrder?: AdyenTerminalOrder | null) =>
  terminalOrder?.status === AdyenTerminalOrderStatusEnum.Placed ||
  terminalOrder?.status === AdyenTerminalOrderStatusEnum.Shipped;

const shippingTerminals = computed<PosTerminal[]>(() =>
  (adyenDashboardData.value?.terminals ?? []).filter((terminal: PosTerminal) =>
    isShipping(terminal.terminalOrder),
  ),
);

const shippingTerminalOrders = computed<AdyenTerminalOrder[]>(() =>
  (adyenDashboardData.value?.terminalOrders ?? []).filter(isShipping),
);

const generateAdyenOnboardingLink = () => {
  loading.value = true;

  generateOnboardingLink().then(() => {
    loading.value = false;
  });
};

if (route.query.payment === 'success') {
  showSuccessModal.value = true;
  setTimeout(() => {
    showSuccessModal.value = false;
  }, 10000);
  router.replace({ name: 'salonized-pay' });
}

const showTerminalOrderTask = computed(
  () => adyenAppState.verificationStatus === 'VALID',
);

// Verified, but nothing ordered or connected yet: nudge them to order one.
const showOrderTerminalBanner = computed(
  () =>
    showTerminalOrderTask.value &&
    !!adyenDashboardData.value &&
    !hasTerminal.value &&
    !adyenDashboardData.value.terminalOrders?.length,
);

const showTerminalOrderConfirmedBanner = computed(
  () =>
    adyenDashboardData.value?.lastTerminalOrderAt &&
    dayjs(adyenDashboardData.value.lastTerminalOrderAt).isBetween(
      dayjs().subtract(4, 'days'),
      dayjs(),
    ),
);

const showTerminalDeliveredBanner = computed(
  () =>
    adyenDashboardData.value?.terminals?.some(
      (terminal: PosTerminal) =>
        terminal.terminalOrder?.status ===
        AdyenTerminalOrderStatusEnum.Delivered,
    ) &&
    !adyenDashboardData.value?.terminals?.some(
      (terminal: PosTerminal) => terminal.status === 'CONNECTED',
    ),
);
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  flex-direction: column;
  gap: $spacing;
}

.payoutSvg {
  position: absolute;
  right: 0;
  bottom: 0;
  height: 82px;
}

.base {
  width: 100%;
  max-width: 910px;
}

.logo {
  border-radius: 100%;
  border: 1px solid var(--color-border);
  max-width: 65px;
}

.header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $spacing;
  flex: 2;

  :not(.smallScreen) & {
    margin-right: $spacing;
  }
}

.companyName {
  width: 100%;
}

.scheduleWrap {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: $spacing;
}

.container {
  display: flex;
  gap: $spacing;

  .base:not(.smallScreen) & {
    align-items: flex-start;
  }

  .base.smallScreen & {
    flex-direction: column;
  }
}

.containerLeft {
  .base:not(.smallScreen) & {
    width: 234px;
    flex-shrink: 0;
  }
}

.containerRight {
  width: 100%;
  // Lets wide content (the payout history table) scroll inside its card instead of
  // pushing the page wider than the dashboard
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: $spacing;
}

</style>

<style lang="scss">
.section-fade-enter-active,
.section-fade-leave-active {
  transition: opacity 0.15s ease;
}

.section-fade-enter-from,
.section-fade-leave-to {
  opacity: 0;
}
</style>
